// Sprint 16 A5: AI Insights Card — purple background, "Ask AI" input
import { Brain, Send } from "lucide-react";
import { useEffect, useState } from "react";
import { apiFetch } from "@/lib/api-client";
import { Skeleton } from "@/components/skeleton";

interface AiInsightsResponse {
  data: {
    content: string;
  };
}

export function AiInsightsCard() {
  const [query, setQuery] = useState("");
  const [insight, setInsight] = useState<string | null>(null);
  const [answer, setAnswer] = useState<string | null>(null);
  const [loadingInsight, setLoadingInsight] = useState(true);
  const [submittingAsk, setSubmittingAsk] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoadingInsight(true);
    setError(null);

    apiFetch<AiInsightsResponse>("/v1/ai/insights", {
      method: "POST",
      body: JSON.stringify({ context: "dashboard" }),
    })
      .then((response) => {
        if (cancelled) return;
        setInsight(response.data.content);
      })
      .catch(() => {
        if (cancelled) return;
        setInsight(null);
        setError("No AI insight available right now.");
      })
      .finally(() => {
        if (cancelled) return;
        setLoadingInsight(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  async function handleAsk() {
    const trimmed = query.trim();
    if (!trimmed || submittingAsk) {
      return;
    }

    setSubmittingAsk(true);
    setError(null);
    try {
      const response = await apiFetch<AiInsightsResponse>("/v1/ai/ask", {
        method: "POST",
        body: JSON.stringify({ question: trimmed }),
      });
      setAnswer(response.data.content);
    } catch {
      setAnswer(null);
      setError("AI answer unavailable right now.");
    } finally {
      setSubmittingAsk(false);
    }
  }

  return (
    <div className="density-card rounded-lg bg-[var(--color-ai-light)] border border-[var(--color-ai-border)] p-5">
      <div className="flex items-center gap-2 mb-3">
        <Brain className="h-5 w-5 text-[var(--color-ai)]" />
        <h3 className="text-sm font-semibold text-[var(--color-ai)]">AI Insights</h3>
      </div>

      <div className="mb-3 min-h-16">
        {loadingInsight ? (
          <div className="space-y-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-4/5" />
          </div>
        ) : (
          <p className="density-text text-sm text-[var(--color-foreground)]">
            {insight ?? "No AI insight available right now."}
          </p>
        )}
      </div>

      <div className="flex items-center gap-2">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Ask AI anything..."
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void handleAsk();
            }
          }}
          className="flex-1 rounded-md border border-[var(--color-ai-border)] bg-[var(--color-card)] px-3 py-1.5 text-sm placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ai)]/30"
        />
        <button
          type="button"
          onClick={() => void handleAsk()}
          disabled={submittingAsk || query.trim().length === 0}
          className="rounded-md bg-[var(--color-ai)] p-1.5 text-white hover:bg-[var(--color-ai)]/90 transition-colors"
          aria-label="Send AI question"
        >
          <Send className="h-4 w-4" />
        </button>
      </div>

      {submittingAsk ? (
        <div className="mt-3 space-y-2">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
        </div>
      ) : null}

      {answer ? (
        <p className="mt-3 text-sm text-[var(--color-foreground)]">{answer}</p>
      ) : null}

      {error ? (
        <p className="mt-3 text-xs text-[var(--color-muted-foreground)]">{error}</p>
      ) : null}
    </div>
  );
}
